import { Card, Modal } from "antd"
import type { BaseOptionType } from "antd/lib/select"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useParams } from "react-router"

import { useAppSelector } from "../../../../../../hooks/use-app-selector.ts"
import { nextPageUrl } from "../../../../../../redux/shared/paged-list.ts"
import { type RootState } from "../../../../../../store.ts"
import { downloadBlob } from "../../../../../../utils/download-file.ts"
import { extractParamsFromNextUrl } from "../../../../../../utils/form/extract-params-from-next-url.ts"
import { serializeData } from "../../../../../../utils/utils"
import { DATE_FORMATS_OBJ } from "../../../../../TeamManagementPage/constants/date-formats.ts"
import { selectTableTabColumns } from "../../../../../User/redux/ui-preferences.selectors.ts"
import { type TabKey } from "../../../../../User/redux/ui-preferences.types.ts"
import { useProjectOutletContext } from "../../../../hooks/use-project-outlet-context.tsx"
import {
  createFinancialTransactionItem,
  deleteFinancialTransactionItem,
  exportFinancialTransactionItems,
  fetchFinancialAlerts,
  fetchFinancialSnapshot,
  fetchFinancialTransactionItems,
  fetchFinancialTransactionItemsFilterOptions,
  patchFinancialTransactionItem
} from "../../../../redux/project-finance/project-finance.thunks.ts"
import { projectSetProp } from "../../../../redux/project.slice.ts"
import { selectProjectWritePermissions } from "../../../../helpers/project-permissions.selectors.ts"
import { type ProjectFinancialTransactionFilters } from "../../../../redux/project.types.ts"
import { type FinancialTransactionItem } from "../../../../types/project-finance.types.tsx"
import { FINANCE_ITEM_TYPES } from "../../../constants/finance-item-constants.ts"
import {
  IssueInvoiceDrawer,
  projectOptionLabel
} from "./ProjectViewFinance/IssueInvoiceDrawer.tsx"
import { ProjectViewFinanceHeader } from "./ProjectViewFinance/ProjectViewFinanceHeader.tsx"
import ProjectViewFinanceList from "./ProjectViewFinance/ProjectViewFinanceList.tsx"

export const ProjectViewFinanceSection = ({
  user,
  projectPermissions,
  getFinancialAlerts,
  data,
  fetching,
  filters,
  setProp,
  getFilterOptions,
  subcontractorsFilters,
  selectedSubcontractor,
  getFinancialTransactionItems,
  exportFinancialTransactionItems,
  createTransactionItem,
  updateTransactionItem,
  removeTransactionItem,
  refetchFinancialSnapshot
}: ConnectedProps<typeof connector>) => {
  const { project } = useProjectOutletContext()
  const id = useParams()?.id as string
  const financialTransactionItems = data?.results || []
  const financialSummary = data?.summary || {
    receivable: 0,
    payable: 0,
    receivable_collected: 0,
    payable_paid: 0
  }
  const count = data?.count || 0
  const userId = user?.id
  // PUT IT IN A SEPARATE UTILITY
  const dateFormat = user?.date_format
  const userDateFormat = dateFormat
    ? DATE_FORMATS_OBJ?.[dateFormat]?.format
    : ""

  const [ordering, setOrdering] = useState<string | null>(null)
  const [editableRow, setEditableRow] = useState<string>("")
  const [isAdding, setIsAdding] = useState(false)
  const [fetchingOptions, setFetchingOptions] = useState(false)
  const [filterOptions, setFilterOptions] = useState<
    Record<string, BaseOptionType[]>
  >({})
  const [columnConfigOpen, setColumnConfigOpen] = useState(false)
  const [generateFromLineId, setGenerateFromLineId] = useState<string | null>(
    null
  )

  const isLocked = !!editableRow || isAdding

  // item_type is a multi-select filter now, but the tab-column preferences
  // (and the new-row default below) only make sense for exactly one type
  // picked — no selection or both selected both mean "All" here, the same as
  // before this filter could hold more than one value.
  const singleItemType =
    filters?.item_type?.length === 1 ? filters.item_type[0] : null

  const tabKey: TabKey =
    singleItemType === FINANCE_ITEM_TYPES.RECEIVABLE
      ? "receivable"
      : singleItemType === FINANCE_ITEM_TYPES.PAYABLE
        ? "payable"
        : "all"
  const tabColumns = useAppSelector((s) =>
    selectTableTabColumns(s, "financial_transactions", tabKey)
  )
  const visibleColumns = useMemo(
    () => tabColumns?.filter((c) => c.visible).map((c) => c.key) ?? undefined,
    [tabColumns]
  )

  const setFilters = useCallback(
    (data: Partial<ProjectFinancialTransactionFilters>) => {
      setProp({
        key: "financialTransactionFilters",
        value: { ...filters, ...data }
      })
    },
    [setProp, filters]
  )

  const onAddTransaction = useCallback(() => {
    setIsAdding(true)
  }, [])

  const handleRefetchFinancialFigures = useCallback(() => {
    refetchFinancialSnapshot(id)
    void getFinancialAlerts(id)
  }, [refetchFinancialSnapshot, getFinancialAlerts, id])

  const handleGetFilterOptions = useCallback(async () => {
    try {
      setFetchingOptions(true)
      const res = await getFilterOptions({ project_id: id }).unwrap()
      setFilterOptions(res)
    } catch {
      // The thunk already reported it; rethrowing only escapes to
      // window.onunhandledrejection.
      setFilterOptions({})
    } finally {
      setFetchingOptions(false)
    }
  }, [getFilterOptions, id])

  const handleExportFinancialTransactionItems = async () => {
    try {
      const blob = await exportFinancialTransactionItems({
        projectId: id,
        visibleColumns
      })?.unwrap()
      downloadBlob(blob, `finances-${project?.lead_name?.toLowerCase()}.xlsx`)
    } catch (e) {
      console.log("Export invoices error: ", e)
    }
  }

  const handleChangePage = useCallback(
    (page: number) => {
      setFilters({ page })
    },
    [setFilters]
  )

  const handleFilterChange = useCallback(
    async (data: Partial<ProjectFinancialTransactionFilters>) => {
      setFilters({ ...data, page: 1 })
    },
    [setFilters]
  )

  const handleRemoveSelectedSubcontractor = useCallback(async () => {
    setProp({ key: "selectedSubcontractor", value: null })
  }, [setProp])

  const handleGetFinancialTransactionItems = useCallback(async () => {
    let params = {}

    if (selectedSubcontractor?.filter_params) {
      params = selectedSubcontractor?.filter_params
    } else {
      params = serializeData({ ...(filters || {}), ordering })
    }

    await getFinancialTransactionItems({
      isMore: false,
      params: { project: id, ...params }
    })
  }, [
    id,
    filters,
    ordering,
    selectedSubcontractor,
    getFinancialTransactionItems
  ])

  const handleUpdateSubcontractorsFilters = useCallback(() => {
    setProp({
      key: "subcontractorsFilters",
      value: { ...subcontractorsFilters, page: 1 }
    })
  }, [subcontractorsFilters, setProp])

  const handleRefreshTransactionItems = useCallback(() => {
    void handleGetFinancialTransactionItems()
    handleRefetchFinancialFigures()
    handleUpdateSubcontractorsFilters()
  }, [
    handleGetFinancialTransactionItems,
    handleRefetchFinancialFigures,
    handleUpdateSubcontractorsFilters
  ])

  const handleGetMoreFinancialTransactionItems = useCallback(async () => {
    const url = nextPageUrl(data)
    if (!url) return

    const serializeFilters =
      extractParamsFromNextUrl<ProjectFinancialTransactionFilters>(url)

    await getFinancialTransactionItems({
      isMore: true,
      params: { project: id, ...serializeFilters }
    })
  }, [data?.next, data?.retryNext, getFinancialTransactionItems, id])

  const handleCreateTransactionItem = async (
    data: FinancialTransactionItem
  ) => {
    try {
      await createTransactionItem({
        ...data,
        project: id
      }).unwrap()
      void handleGetFinancialTransactionItems()
      handleUpdateSubcontractorsFilters()
      handleRefetchFinancialFigures()
      setIsAdding(false)
    } catch (e) {
      console.log("Create transaction item error: ", e)
    }
  }

  const handleUpdateTransactionItem = async (
    data: FinancialTransactionItem
  ) => {
    try {
      await updateTransactionItem({ ...data, id: editableRow })
      handleUpdateSubcontractorsFilters()
      handleRefetchFinancialFigures()
      return setEditableRow("")
    } catch (e) {
      console.log("Update transaction item error: ", e)
    }
  }

  const handleDeleteTransactionItem = async (itemId: string) => {
    Modal.confirm({
      title: "Delete Financial Transaction Item",
      content: "Are you sure you want to delete this transaction item?",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await removeTransactionItem(itemId).unwrap()

          handleUpdateSubcontractorsFilters()
          handleRefetchFinancialFigures()
        } catch (e) {
          console.log("Delete transaction item error: ", e)
        }
      }
    })
  }

  const onSelectRow = (record: FinancialTransactionItem, action: string) => {
    switch (action) {
      case "generate_invoice":
        return setGenerateFromLineId(record?.id)
      case "edit_row":
        return setEditableRow(record?.id)
      case "update":
        return handleUpdateTransactionItem(record)
      case "cancel":
        return setEditableRow("")
      case "create":
        return handleCreateTransactionItem(record)
      case "cancel_create":
        return setIsAdding(false)
      case "delete_row":
        return handleDeleteTransactionItem(record?.id)
    }
  }

  useEffect(() => {
    void handleGetFinancialTransactionItems()
  }, [id, handleGetFinancialTransactionItems])

  useEffect(() => {
    void handleGetFilterOptions()
  }, [handleGetFilterOptions])

  useEffect(() => {
    if (id) void getFinancialAlerts(id)
  }, [getFinancialAlerts, id])

  return (
    <Card
      className="overflow-clip"
      classNames={{ body: "p-0", header: "p-2" }}
      title={
        <ProjectViewFinanceHeader
          selectedSubcontractor={selectedSubcontractor}
          filters={filters}
          options={filterOptions}
          fetchingOptions={fetchingOptions}
          isLocked={isLocked}
          isEmptyList={financialTransactionItems?.length === 0}
          onFilterChange={handleFilterChange}
          onExport={handleExportFinancialTransactionItems}
          onClearSubcontractor={handleRemoveSelectedSubcontractor}
          onOpenColumnConfig={() => setColumnConfigOpen(true)}
        />
      }
    >
      <ProjectViewFinanceList
        userId={userId}
        userDateFormat={userDateFormat}
        projectPermissions={projectPermissions}
        projectId={id}
        fetching={fetching}
        financialTransactionItems={financialTransactionItems}
        financialSummary={financialSummary}
        itemType={singleItemType}
        editableRow={editableRow}
        isAdding={isAdding}
        next={data?.next}
        retryNext={data?.retryNext}
        count={count}
        options={filterOptions}
        configOpen={columnConfigOpen}
        onLoadMore={handleGetMoreFinancialTransactionItems}
        onOrderingChange={setOrdering}
        generateInvoiceAllowed={
          projectPermissions?.invoices?.project_invoice_edit
        }
        onAddTransaction={onAddTransaction}
        onSelectRow={onSelectRow}
        onPageChange={handleChangePage}
        onCloseColumnConfig={() => setColumnConfigOpen(false)}
        onRefresh={handleRefreshTransactionItems}
      />

      <IssueInvoiceDrawer
        open={!!generateFromLineId}
        projectId={id}
        projectLabel={projectOptionLabel(project)}
        preselectedLineId={generateFromLineId ?? undefined}
        onClose={() => setGenerateFromLineId(null)}
        onSaved={() => {
          setGenerateFromLineId(null)
          void handleGetFinancialTransactionItems()
        }}
      />
    </Card>
  )
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.userInfo,
  fetching: state.project.fetchingSection,
  data: state.project.financialTransactionItems,
  filters: state.project.financialTransactionFilters,
  subcontractorsFilters: state.project.subcontractorsFilters,
  projectPermissions: selectProjectWritePermissions(state),
  selectedSubcontractor: state?.project?.selectedSubcontractor
})

const mapDispatchToProps = {
  getFinancialTransactionItems: fetchFinancialTransactionItems,
  exportFinancialTransactionItems,
  getFilterOptions: fetchFinancialTransactionItemsFilterOptions,
  createTransactionItem: createFinancialTransactionItem,
  updateTransactionItem: patchFinancialTransactionItem,
  removeTransactionItem: deleteFinancialTransactionItem,
  refetchFinancialSnapshot: fetchFinancialSnapshot,
  getFinancialAlerts: fetchFinancialAlerts,
  setProp: projectSetProp
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProjectViewFinanceSection)
